/**
 * Centered-modal primitive — the shared geometry behind every desktop overlay
 * (INVENTORY-DESKTOP §4). The desktop equivalent of `components/Sheet.tsx`;
 * neither imports the other, and the mobile tree never renders this.
 *
 * Frozen shape (prototype lines 798-800, 876, 957, 1003, 1028, 1078):
 *   overlay  `position:fixed;inset:0;z-index:80;background:var(--gt-overlay);
 *             display:flex;align-items:center;justify-content:center;
 *             padding:24px;cursor:pointer`
 *   panel    `width:Wpx;max-width:100%;max-height:92vh;overflow:auto;
 *             background:var(--gt-card);border:1px solid var(--gt-border);
 *             box-shadow:var(--gt-shadow-modal);display:flex;
 *             flex-direction:column;cursor:auto`
 *   close    backdrop click, gated on `ev.target === ev.currentTarget`
 *
 * NO ENTRY ANIMATION. Mobile sheets slide in with `animation:gt-sheet .18s`;
 * desktop modals just appear. The `cursor:pointer` / `cursor:auto` pair IS the
 * desktop affordance for "click outside to close" — keep both.
 *
 * WIDTHS IN USE (§4): 960 Filter Bar · 880 Browse Steam · 700 Playtest detail ·
 * 640 What's New · 620 Steam sync · 580 Add playtest · 520 Tracked list.
 *
 * THE FILTER BAR VARIANT is why the overlay is parameterised: it sits at
 * `z-index:70` (UNDER the other modals), paints `rgba(3,3,6,.75)` instead of
 * `--gt-overlay`, aligns to `flex-start` with `padding:60px 20px`, scrolls the
 * OVERLAY rather than the panel, and has no `max-height`. Everything else takes
 * the defaults.
 *
 * DEVIATION — ESCAPE TO CLOSE. The prototype has no `Escape` handler anywhere
 * (§2.4: zero occurrences in 2,547 lines); modals close only via ✕ or a
 * backdrop click. That is a real regression from mobile's swipe-dismissable
 * sheets, so this primitive adds it, as §2.4 recommends. Only the TOP-MOST open
 * overlay responds, so a Filter Bar panel opened over another modal closes
 * itself first. Pass `escapeCloses={false}` to opt out.
 *
 * The stack has a second client since W4: the game detail FRIENDS drawer, which
 * is an overlay below 1080px and a static column above it. It registers through
 * the exported `useEscape` rather than adding a listener of its own — see that
 * hook for why LIFO is the right precedence and not a compromise.
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/** LIFO stack of open modals; only the last one hears Escape. */
const escStack: Array<() => void> = [];
let escWired = false;

function onEscape(ev: KeyboardEvent): void {
  if (ev.key !== 'Escape') return;
  const top = escStack[escStack.length - 1];
  if (!top) return;
  ev.preventDefault();
  top();
}

function pushEsc(close: () => void): () => void {
  escStack.push(close);
  if (!escWired) {
    window.addEventListener('keydown', onEscape);
    escWired = true;
  }
  return () => {
    const i = escStack.lastIndexOf(close);
    if (i >= 0) escStack.splice(i, 1);
    if (!escStack.length && escWired) {
      window.removeEventListener('keydown', onEscape);
      escWired = false;
    }
  };
}

/**
 * Join the Escape stack for as long as `enabled` holds.
 *
 * Exported because `Modal` is no longer the only client. The FRIENDS panel's
 * narrow-width drawer (`DetailFriendsPanel`) is a dismissable overlay that is
 * not a modal — it is the same element that is a static column above 1080px —
 * and giving it its own `keydown` listener would put two handlers in a race on
 * one key. It registers here instead, so ordinary LIFO decides: a modal opened
 * over the drawer closes first, exactly as the prototype's own comment says it
 * should (`Games Tonight.dc.html:1509`), and the drawer is next.
 *
 * A hook rather than the raw `pushEsc` so that no call site can register without
 * also unregistering. `close` is read through a ref, so re-rendering never churns
 * the stack and the stack always invokes the LATEST handler.
 */
export function useEscape(close: () => void, enabled = true): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!enabled) return;
    return pushEsc(() => closeRef.current());
  }, [enabled]);
}

export interface ModalHeaderProps {
  /** pixel-font title, e.g. `PLAYTEST` */
  title: ReactNode;
  /** title colour — default `var(--acc)` */
  titleClr?: string | undefined;
  /** pixel title size — default `9.5px` (§4 "titles pixel 9.5px") */
  titleFs?: string | undefined;
  /** mono note that fills the row */
  note?: ReactNode;
  /** rendered between the note and the ✕ (e.g. What's New "CLEAR ALL") */
  extra?: ReactNode;
  /** stick the header to the top of the panel's own scroller (§1.5) */
  sticky?: boolean | undefined;
}

export interface ModalProps {
  onClose: () => void;
  children: ReactNode;
  /** panel width in px — one of the seven in §4 */
  width: number;
  /** 80 for everything except the Filter Bar panel (70) */
  zIndex?: number | undefined;
  /** overlay paint — Filter Bar passes `rgba(3,3,6,.75)` */
  overlay?: string | undefined;
  /** overlay cross-axis alignment — Filter Bar passes `flex-start` */
  align?: 'center' | 'flex-start' | undefined;
  /** overlay padding — default `24px`, Filter Bar `60px 20px` */
  padding?: string | undefined;
  /** panel cap — default `92vh`; What's New passes `90vh`, Filter Bar `null` */
  maxHeight?: string | null | undefined;
  /** scroll the OVERLAY instead of the panel (Filter Bar) */
  scrollOverlay?: boolean | undefined;
  /** `data-screen-label`, kept from the prototype for screenshot tooling */
  label?: string | undefined;
  /** the standard header row; omit to render a fully custom head in `children` */
  header?: ModalHeaderProps | undefined;
  /** merged into the panel, after the frozen base */
  panelStyle?: CSSProperties | undefined;
  /** see the DEVIATION note above */
  escapeCloses?: boolean | undefined;
}

export function Modal({
  onClose,
  children,
  width,
  zIndex,
  overlay,
  align,
  padding,
  maxHeight,
  scrollOverlay,
  label,
  header,
  panelStyle,
  escapeCloses,
}: ModalProps): JSX.Element {
  // One registration path for every client — see `useEscape`.
  useEscape(onClose, escapeCloses !== false);

  const cap = maxHeight === undefined ? '92vh' : maxHeight;

  const panel: CSSProperties = {
    width,
    maxWidth: '100%',
    background: 'var(--gt-card)',
    border: '1px solid var(--gt-border)',
    boxShadow: 'var(--gt-shadow-modal)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    cursor: 'auto',
    ...(cap && !scrollOverlay ? { maxHeight: cap, overflow: 'auto' } : null),
  };

  return (
    <div
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
      data-screen-label={label}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: zIndex ?? 80,
        background: overlay ?? 'var(--gt-overlay)',
        display: 'flex',
        alignItems: align ?? 'center',
        justifyContent: 'center',
        padding: padding ?? '24px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        ...(scrollOverlay ? { overflowY: 'auto' } : null),
      }}
    >
      <div style={{ ...panel, ...panelStyle }}>
        {header ? <ModalHeader {...header} onClose={onClose} /> : null}
        {children}
      </div>
    </div>
  );
}

/**
 * The standard header row (§4): `padding:15px 18px`, 1px `--gt-line` bottom,
 * pixel 9.5px title + mono note + a `2px 6px` mono-15px ✕.
 *
 * Exported so a modal that needs a fully custom head (What's New's sticky bar
 * with CLEAR ALL, the Filter Bar's `+ FILTERS` / match-count row) can still
 * reuse the ✕ geometry by composing rather than forking.
 */
export function ModalHeader({
  title,
  titleClr,
  titleFs,
  note,
  extra,
  sticky,
  onClose,
}: ModalHeaderProps & { onClose: () => void }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '15px 18px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        flex: 'none',
        ...(sticky ? { position: 'sticky', top: 0, zIndex: 1 } : null),
      }}
    >
      <div
        style={{
          fontFamily: 'var(--gt-font-pixel)',
          fontSize: titleFs ?? '9.5px',
          color: titleClr ?? 'var(--acc)',
          transform: 'translateY(0.13em)',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: 'var(--gt-font-mono)',
          fontSize: '11px',
          color: 'var(--gt-faint)',
          flex: 1,
          minWidth: 0,
        }}
      >
        {note}
      </div>
      {extra}
      <ModalClose onClose={onClose} />
    </div>
  );
}

/** The ✕ affordance on its own — `mono 15px var(--gt-text-5)`, `padding:2px 6px`. */
export function ModalClose({ onClose, tip }: { onClose: () => void; tip?: string | undefined }): JSX.Element {
  return (
    <div
      onClick={onClose}
      data-tip={tip}
      data-tip-pos={tip ? 'r' : undefined}
      style={{
        fontFamily: 'var(--gt-font-mono)',
        fontSize: '15px',
        color: 'var(--gt-text-5)',
        padding: '2px 6px',
        cursor: 'pointer',
        flex: 'none',
        lineHeight: 1,
      }}
    >
      ✕
    </div>
  );
}

export default Modal;
